import {
	Form,
	FormItem,
	ReorderableList,
	Select,
	SelectItem
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { guid, newGuid } from "client_legacy/ndaw/guid";
import {
	TileEditMode
} from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { OwnRssTabContent } from "./OwnRssTabContent";
import { PredefinedRssTabContent } from "./PredefinedRssTabContent";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { SharePointFeedInformationDialog } from "../Dialogs/SharePointFeedInformationDialog";
import {useCreateTile} from "../hooks/useCreateTile";
import {useUpdateTile} from "../hooks/useUpdateTile";
import {
	AddTilesResponseModel,
	RssAppTab,
	OwnRssAppTab,
	RssFeedConfigurationSource,
	RssUpdateTileModel,
	RssDetailsTileModel,
	RssFeedModel,
	RssTab,
	RssCreateTileModel,
	createRssTile,
	updateRssTile
} from "client/Api/Workspace/workspace";
import { DEFAULT_FEED_TYPE } from "client/Features/Apps/Store/EditApp/Apps/EditRssApp";

const MAX_TAB_NUMBER = 20;
function isOwnRssAppTab(tab: RssAppTab): tab is OwnRssAppTab {
	return tab.configurationSource === RssFeedConfigurationSource.Own;
};

type RssEditFormData = EditTileFromData<RssUpdateTileModel>;

const validationBuilder: IValidationConfiguration<RssEditFormData> = createValidationConfiguration<RssEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation: IObjectValidatorBuilder<RssAppTab> = validationBuilder.forCollection("tabs")
	.forItems(
		(x) => x.uniqueId
	);

const ownTabValidation: IObjectValidatorBuilder<OwnRssAppTab> = tabValidation.whenType(isOwnRssAppTab);

ownTabValidation.forScalar("feedUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: false
		})
	);

ownTabValidation.forScalar("siteUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: false
		})
	);

ownTabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

const NEW_CONFIGURATION_SOURCE_ID = 0;

export type EditRssTileProps = {
	initialModel: RssDetailsTileModel;
	mode: TileEditMode;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditRssTile = forwardRef((props: EditRssTileProps, ref: Ref<EditTileHandle>): ReactElement => {
	const {
		initialModel,
		mode,
		onIsLoadingChange,
		isLoading,
		onAfterSave,
	} = props;

	const createNewOwnTab = (): OwnRssAppTab => ({
		configurationSource: RssFeedConfigurationSource.Own,
		uniqueId: newGuid(),
		name: "",
		feedUrl: "",
		feedType: DEFAULT_FEED_TYPE,
		siteUrl: ""
	});

	const [isSharePointFeedInformationDialogOpen, setIsSharePointFeedInformationDialogOpen] = useState<boolean>(false);
	const [formData, setFormData] = useState<RssEditFormData>(() => {
		const {
			tabs
		} = initialModel;
		return ({
			...initialGenericState(initialModel),
			tabs: tabs.length
				? tabs.map(
					x => convertToUpdateRssTabsModel(x)
				)
				: [createNewOwnTab()]
		});
	});
	const formHandler: IFormHandler<RssEditFormData> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);

	const predefinedAppTabsData: Map<number, RssFeedModel> = useMemo(
		(): Map<number, RssFeedModel> => {
			const newData: Map<number, RssFeedModel> = new Map();
			initialModel.predefinedFeeds
				.forEach(x => {
					newData.set(
						x.id,
						x
					);
				});

			return newData;
		},
		[initialModel]
	);

	const configurationSourceOptions: SelectItem<number>[] = useMemo(
		() => {
			const options: SelectItem<number>[] = initialModel.predefinedFeeds
				.map(
					x => ({
						text: x.name,
						value: x.id
					})
				);
			options.unshift({
				text: localize("_New"),
				value: NEW_CONFIGURATION_SOURCE_ID
			});

			return options;
		},
		[initialModel]
	);

	const { createHandler } = useCreateTile<RssCreateTileModel>(
		formHandler,
		createRssTile,
		isLoading,
		onIsLoadingChange,
		mode,
		onAfterSave);

	const { updateHandler } = useUpdateTile<RssUpdateTileModel>(
		formHandler,
		updateRssTile,
		isLoading,
		onIsLoadingChange,
		mode,
		onAfterSave
	);

	useImperativeHandle(
		ref,
		() => ({ createHandler, updateHandler })
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			setFormData(
				(formState) => {
					const tabsCopy: RssAppTab[] = [...formState.tabs];
					const item: RssAppTab = tabsCopy.splice(fromIndex, 1)[0];
					tabsCopy.splice(toIndex, 0, item);

					return {
						...formState,
						tabs: tabsCopy
					};
				}
			);
		},
		[]
	);

	const addTabHandler = (): void => {
		const tabsHandler: IValueInputAttachable<RssAppTab[]> = formHandler.for("tabs");

		tabsHandler.reducer
			.push(createNewOwnTab());
		tabsHandler.validation
			.triggerInputFinished();
	};

	const removeTabHandler = (index: number): void => {
		if (formData.tabs.length > 1) {
			formHandler.for("tabs")
				.reducer
				.removeAt(index);
		}
	};

	/**
	 * Change the type and data of the tab,
	 * regarding the selected configuration source
	 */
	const configurationSourceChangeHandler = (
		sourceId: number,
		uniqueId: string
	): void => {
		setFormData(prevState => {
			return {
				...prevState,
				tabs: prevState.tabs
					.map(
						x => {
							if (x.uniqueId === uniqueId) {
								if (sourceId === NEW_CONFIGURATION_SOURCE_ID) {
									return createNewOwnTab();
								} else {
									return {
										configurationSource: RssFeedConfigurationSource.Predefined,
										feedId: sourceId,
										uniqueId: newGuid()
									};
								}
							}
							return x;
						}
					)
			};
		});
	};

	return (
		<>
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<FormItem
					hasNoMargin
				>
					<ReorderableList
						onItemMove={itemMoveHandler}
					>
						{formHandler.for("tabs")
							.map((tabHandler, index) => {
									const {
										uniqueId
									} = tabHandler.value;

									return (
										<ReorderableList.PanelItem
											{...tabHandler.attachToReorderableListPanelItem(true)}
											key={uniqueId}
											header={
												<Select
													value={
														tabHandler.whenType(
															isOwnRssAppTab,
															() => 0,
															predefinedTabHandler => predefinedTabHandler.value
																.feedId
														)
													}
													onChange={(sourceId: number) => {
														configurationSourceChangeHandler(sourceId, uniqueId);
														tabHandler.validation.triggerInputFinished();
													}}
													options={configurationSourceOptions}
													menuTitle={localize("_Source")}
												/>
											}
											onRemove={() => removeTabHandler(index)}
										>
											{
												tabHandler.whenType(
													isOwnRssAppTab,
													ownTabHandler => (
														<OwnRssTabContent
															ownTabHandler={ownTabHandler}
															onOpenDialog={() => setIsSharePointFeedInformationDialogOpen(true)}
														/>
													),
													predefinedTabHandler => (
														<PredefinedRssTabContent
															predefinedTabHandler={predefinedTabHandler}
															predefinedAppTabsData={predefinedAppTabsData}
														/>
													)
												)
											}
										</ReorderableList.PanelItem>
									);
								}
							)}
					</ReorderableList>
					{formData.tabs.length < MAX_TAB_NUMBER && (
						<ReorderableList.AddButton
							trackingId="add-tab-button"
							onClick={addTabHandler}
						/>
					)}
				</FormItem>
			</Form>
			{isSharePointFeedInformationDialogOpen && (
				<SharePointFeedInformationDialog
					onClose={() => setIsSharePointFeedInformationDialogOpen(false)}
				/>
			)}
		</>
	);
});

EditRssTile.displayName = "EditRssTile";

function convertToUpdateRssTabsModel(
	tab: RssTab
): RssAppTab {
	switch (tab.configurationSource) {
		case RssFeedConfigurationSource.Own:
			return {
				configurationSource: RssFeedConfigurationSource.Own,
				uniqueId: guid(tab.uniqueId),
				name: tab.name,
				feedUrl: tab.feedUrl,
				feedType: tab.feedType,
				siteUrl: tab.siteUrl
			};
		case RssFeedConfigurationSource.Predefined:
			return {
				configurationSource: RssFeedConfigurationSource.Predefined,
				uniqueId: guid(tab.uniqueId),
				feedId: tab.feedId
			};
		default:
			never(tab);
	}
}
